import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, ViewChildren, QueryList, Type, Injector, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PluginEntry, WizardBuilderBaseComponent } from '../../base/wizard-builder-base.component';
import { IWizardPluginComponent } from '../../interfaces/wizard-plugin-component.interface';
import { IPluginPreviewComponent } from '../../interfaces/plugin-preview-component.interface';
import { ComponentHostDirective } from '../../decoration-base/component-host.directive';
import { DynamicComponentService } from '../../decoration-base/dynamic-component.service';
import { ErrorHandlerService } from '../../decoration-base/error-handler.service';
import { DecoratorService } from '../../decoration-base/decorator.service';
import { BasePluginData } from '../../models/wizard-plugin-models/base-plugin-data';
import { ResourceDraft } from '../../models/resource-draft';
import { ResourceConfig } from '../../models/configs/resource-config';
import { ResourceMetaData } from '../../models/resource-meta-data';
import { ResourceCreatorService } from '../../services/resource-creator.service';
import { ResourceMasterDataService } from '../../services/resource-master-data.service';
import { CLOUD_RESOURCE_KEY } from '../../decorators/cloud-resource';
import { IShortLogger, Log } from '../../services/logger.service';

@Component({
  selector: 'app-wizard-builder',
  templateUrl: './wizard-builder.component.html',
  styleUrls: ['./wizard-builder.component.css']
})
export class WizardBuilderComponent extends WizardBuilderBaseComponent implements OnInit, AfterViewInit {

  // from Traininer requisitioin view ----wizard.component.ts
  @Input() resourceNameInput: string = 'Unknown';

  @Input() navigateToRequstedForm: boolean | undefined = false;

  @Output() wizardClosed = new EventEmitter<any>();
  savedResourcetemplateIds: string = ""; // Used to store resource template IDs after creation
  // from Traininer requisitioin view

  @ViewChild('pluginHost', { static: false })
  pluginHost!: ComponentHostDirective;

  /**
   * ViewChildren decorator to get references to all preview host directives.
   * Each plugin gets its own preview host to maintain individual previews.
   */
  @ViewChildren('previewHost') previewHosts!: QueryList<ComponentHostDirective>;

  /**
   * Indicates whether plugins have been loaded
   */
  pluginsLoaded: boolean = false;

  /**
   * Reference to the currently active plugin component instance
   */
  private currentPluginRef: IWizardPluginComponent | null = null;

  /**
   * Set to track which plugins have preview components.
   * This prevents repeated instance creation during change detection.
   */
  private pluginPreviewCapability: Map<number, boolean> = new Map();

  /**
   * Set to track which plugins have their previews loaded.
   * This prevents unnecessary re-loading of unchanged previews.
   * Key is the plugin name, not the index, since indices may not be consistent with *ngIf
   */
  private loadedPreviewPlugins: Set<string> = new Set();

  currentClass: string = 'col-md-5';

  // Logger instance (override from base class)
  protected override readonly logger: IShortLogger<WizardBuilderComponent> = Log.create(WizardBuilderComponent);

  finishButtonName: string = 'Finish';
  finishButtonToolTip: string = 'Finish';
  showSaveAndForwardButton: boolean = false;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    decorationService: DecoratorService,
    dynamicService: DynamicComponentService,
    errorHandler: ErrorHandlerService,
    private cdRef: ChangeDetectorRef,
    public service: ResourceCreatorService,
    private injectService: ResourceMasterDataService,
    private injector: Injector
  ) {
    super(decorationService, dynamicService, errorHandler, Log.create(WizardBuilderComponent));
  }

  override ngOnInit(): void {
    this.subscription.add(
      this.route.params.subscribe(params => {
        this.resourceName = params['name'] || 'Unknown';


        if (!this.resourceNameInput && this.route.snapshot.params['name']) {
          this.resourceNameInput = this.route.snapshot.params['name'];
        }
        if (this.resourceName == 'Unknown') {
          this.resourceName = this.resourceNameInput;
        }
        this.logger.i(`Resource name received as InPut: ${this.resourceNameInput}`);

        this.logger.i(`Resource name received as: ${this.resourceName}`);
        this.loadAvailableResources();
      })
    );


    if (this.resourceName === 'Requisition') {
      this.currentClass = 'col-md-5';
      this.finishButtonName = 'Save as Draft';
      this.finishButtonToolTip = 'Save as Draft';
      this.showSaveAndForwardButton = true;

    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loadResourceComponent();
    });
  }

  /**
   * Override the parent method to also cache preview capabilities after plugins are loaded
   */
  protected override loadResourceComponent(): void {
    super.loadResourceComponent();

    // Cache preview capabilities after plugins are loaded
    if (this.plugins.length > 0) {
      this.cachePluginPreviewCapabilities();
    }
  }

  /**
   * Load available resources for reference
   */
  private loadAvailableResources(): void {
    const resourceComponents = this.decorationService.getDecoratedComponents(CLOUD_RESOURCE_KEY);
    this.availableResources = resourceComponents.map(comp => ({
      componentType: comp,
      config: this.decorationService.getComponentConfig<ResourceConfig>(comp, CLOUD_RESOURCE_KEY)!
    })).filter(entry => entry.config !== undefined);
  }

  /**
   * Override createPreviewContent to avoid setting data on temporary instances.
   * This prevents warnings from plugins that access services in their setPluginData method.
   *
   * @param pluginEntry The plugin registry entry
   * @param pluginData The plugin data (not used in this override)
   * @returns Preview component type or undefined
   */
  protected override createPreviewContent(
    pluginEntry: PluginEntry,
    pluginData: BasePluginData
  ): Type<IPluginPreviewComponent> | undefined {
    return this.errorHandler.tryExecute(() => {
      // Create a temporary instance only to get the preview component type
      const tempInstance = new (pluginEntry.componentType as any)();

      // DO NOT call setPluginData to avoid triggering service dependencies
      // Just get the preview component type
      if (tempInstance.asPreview) {
        return tempInstance.asPreview();
      }

      return undefined;
    }, 'createPreviewContent', undefined);
  }

  /**
   * Cache preview capabilities for all plugins to avoid repeated checks during change detection.
   * This method should be called after plugins are loaded.
   */
  private cachePluginPreviewCapabilities(): void {
    this.pluginPreviewCapability.clear();
    this.plugins.forEach((plugin, index) => {
      const hasPreviewMethod = 'asPreview' in plugin.componentType.prototype;
      this.pluginPreviewCapability.set(index, hasPreviewMethod);
    });
  }

  /**
   * Get the current plugin instance
   * Overrides the abstract method from WizardBuilderBaseComponent
   */
  protected override getCurrentPluginInstance(): IWizardPluginComponent | null {
    return this.currentPluginRef;
  }

  /**
   * Load the current plugin component using ComponentHostDirective
   * Implements the abstract method from WizardBuilderBaseComponent
   */
  protected loadCurrentPlugin(): void {
    this.errorHandler.tryExecute(() => {
      if (!this.pluginHost) {
        this.logger.w('Plugin host not available - will retry after a delay');
        setTimeout(() => {
          if (this.pluginHost) {
            this.loadCurrentPlugin();
          } else {
            this.logger.e('Plugin host still not available after delay');
          }
        }, 100);
        return;
      }

      const currentPlugin = this.getCurrentPluginEntry();
      if (!currentPlugin) {
        this.logger.e('No current plugin entry');
        return;
      }

      try {

        const pluginName = currentPlugin.config.dataModelType.name;
        var pluginData = this.pluginData[pluginName];
        // if (pluginName == 'BillingDetailsData') {

        //   const reqData = this.pluginData['RequisitionData'] as RequisitionData;
        //   const chkBillingdata = new BillingDetailsData();
        //   if (pluginData) {
        //     Object.assign(chkBillingdata, pluginData);
        //   }
        //   // else {
        //     if (reqData && reqData.customerDetails) {
        //       if (chkBillingdata.billingDetails) {

        //         chkBillingdata.billingDetails = { billingAddress: chkBillingdata.billingDetails.billingAddress.length>0? chkBillingdata.billingDetails.billingAddress: reqData.customerDetails.address,
        //           gstNo: chkBillingdata.billingDetails.gstNo.length>0? chkBillingdata.billingDetails.gstNo: reqData.customerDetails.gstNumber,
        //           paymentTerms: chkBillingdata.billingDetails.paymentTerms.length>0? chkBillingdata.billingDetails.paymentTerms:'',
        //           estimateAmount: chkBillingdata.billingDetails.estimateAmount>0? chkBillingdata.billingDetails.estimateAmount:0 };

        //       }
        //     }
        //   // }

        //   pluginData = chkBillingdata;
        // }
        const componentRef = this.loadPluginComponent(this.pluginHost, currentPlugin, pluginData);

        if (componentRef) {
          this.currentPluginRef = componentRef.instance;
          this.cdRef.detectChanges();
          this.updatePreview();
        }
      } catch (error) {
        this.logger.e('Error creating plugin component:', error);
      }
    }, 'loadCurrentPlugin', null);
  }

  /**
   * Returns the stable plugin data key for a plugin entry.
   * Uses pluginStepName (string literal) instead of dataModelType.name which is
   * mangled by esbuild minification in production/QA builds.
   */
  private getPluginDataKey(plugin: PluginEntry): string {
    return new plugin.config.dataModelType().pluginStepName;
  }

  /**
   * Update preview components with current data using multiple hosts.
   * Implements the abstract method from WizardBuilderBaseComponent.
   *
   * This implementation maintains individual previews for each plugin,
   * preventing the overwriting issue that occurred with a single host.
   */
  protected updatePreview(): void {
    this.errorHandler.tryExecute(() => {
      if (!this.hasPluginData()) return;

      // Force Angular to render *ngIf-gated previewHost elements before querying them
      this.cdRef.detectChanges();

      if (!this.previewHosts || this.previewHosts.length === 0) {
        this.logger.w('Preview hosts not available');
        return;
      }

      this.logger.i('Updating previews with plugin data');

      const hostArray = this.previewHosts.toArray();
      let hostIndex = 0;

      // Iterate through plugins and update previews only for those with data
      this.plugins.forEach((plugin, pluginIndex) => {
        const pluginName = this.getPluginDataKey(plugin);
        const pluginData = this.pluginData[pluginName];

        // Only process if plugin has data
        if (pluginData && hostIndex < hostArray.length) {
          const host = hostArray[hostIndex];
          this.updatePluginPreview(host, pluginIndex, pluginName);
          hostIndex++;
        }
      });

      this.cdRef.detectChanges();
    }, 'updatePreview', null);
  }

  /**
   * Update a specific plugin's preview in its dedicated host.
   * Only updates if the preview hasn't been loaded yet to avoid unnecessary re-renders.
   *
   * @param host The ComponentHostDirective for this plugin's preview
   * @param pluginIndex The index of the plugin in the plugins array
   * @param pluginName The name of the plugin data model
   */
  private updatePluginPreview(host: ComponentHostDirective, pluginIndex: number, pluginName: string): void {
    // Skip if this preview is already loaded (using plugin name as key)
    if (this.loadedPreviewPlugins.has(pluginName)) {
      return;
    }

    const pluginEntry = this.plugins[pluginIndex];
    if (!pluginEntry) {
      this.logger.e(`Plugin entry not found for index ${pluginIndex}`);
      return;
    }

    const pluginData = this.pluginData[pluginName];
    if (!pluginData) {
      this.logger.e(`Plugin data not found for ${pluginName}`);
      return;
    }

    // Create and load the preview component
    const previewComponentType = this.createPreviewContent(pluginEntry, pluginData);
    if (previewComponentType) {
      const componentRef = this.loadPreviewComponent(host, previewComponentType, pluginData);
      if (componentRef) {
        this.loadedPreviewPlugins.add(pluginName);
        this.logger.i(`Loaded preview for plugin ${pluginName} at index ${pluginIndex}`);
      }
    } else {
      this.logger.i(`No preview component available for plugin ${pluginName}`);
    }
  }

  /**
   * Check if a plugin at a specific index has data available and a preview component.
   * Used to control visibility of preview sections in the template.
   *
   * This method is optimized to avoid creating plugin instances during change detection.
   * Preview capability is checked once and cached.
   *
   * @param index The plugin index to check
   * @returns True if the plugin has data and can show a preview, false otherwise
   */
  hasPluginDataForIndex(index: number): boolean {
    if (index < 0 || index >= this.plugins.length) {
      return false;
    }

    const plugin = this.plugins[index];
    const pluginName = this.getPluginDataKey(plugin);
    const pluginData = this.pluginData[pluginName];

    // First check: does the plugin have data?
    if (!pluginData) {
      return false;
    }

    // Second check: does the plugin have preview capability? (cached)
    if (!this.pluginPreviewCapability.has(index)) {
      // Check if the plugin has an asPreview method without creating an instance
      const hasPreviewMethod = 'asPreview' in plugin.componentType.prototype;
      this.pluginPreviewCapability.set(index, hasPreviewMethod);
    }

    return this.pluginPreviewCapability.get(index) || false;
  }

  /**
   * Override the clearAllComponentHosts method to also clear preview tracking.
   * This ensures proper cleanup when switching between resources or resetting the wizard.
   */
  protected override clearAllComponentHosts(): void {
    super.clearAllComponentHosts();
    this.loadedPreviewPlugins.clear();
    this.pluginPreviewCapability.clear();
  }

  /**
   * Save data from the current plugin and set the UI name from plugin configuration.
   * Overrides the parent method to enhance plugin data with UI display name.
   */
  protected override saveCurrentPluginData(): void {
    this.errorHandler.tryExecute(() => {
      // Get the current plugin component instance from the active component refs
      const pluginInstance = this.getCurrentPluginInstance();

      if (pluginInstance) {
        const pluginData = pluginInstance.getPluginData();
        const pluginName = pluginData.pluginStepName;

        // Find the current plugin entry to get the UI name
        const currentPlugin = this.getCurrentPluginEntry();
        if (currentPlugin) {
          // Set the UI display name from the plugin configuration
          pluginData.name = currentPlugin.config.name;
        }

        // Store the data
        this.pluginData[pluginName] = pluginData;
        this.logger.i(`Saved data for plugin ${pluginName}:`, pluginData);

      }
    }, 'saveCurrentPluginData', null);

    // Clear the loaded preview status to force refresh on next update
    this.loadedPreviewPlugins.clear();

    // Update previews immediately after saving
    this.updatePreview();
  }

  /**
   * Handle wizard completion
   * Implements the abstract method from WizardBuilderBaseComponent
   */
  protected async onWizardComplete(finalData: { [key: string]: BasePluginData }, action: string): Promise<any> {
    this.logger.i('Wizard completed with data:', finalData);

    if (!this.resourceConfig?.service) {
      this.logger.i("stringify data :: ", JSON.stringify(finalData));
      this.service.create(finalData, this.resourceConfig).subscribe((res) => {
        this.logger.i("Response from api:: ", res);
        this.logger.i("stringify data response :: ", JSON.stringify(res));
        if (this.resourceNameInput == "Virtual Machine" || this.resourceNameInput == "User Account") {
          this.savedResourcetemplateIds = res.id;

          this.logger.i("Resource template ID saved: ", this.savedResourcetemplateIds);
          this.navigateToRequstedForm = false;
          const result = {
            success: true,
            message: 'Wizard completed successfully',
            resourceTemplatedId: this.savedResourcetemplateIds,
            showWizardPopup: false,
          };
          this.wizardClosed.emit(result); // Emit to parent
        }

        this.snackBar.open(`Request to create ${this.resourceConfig?.name} received. You can now proceed to provision the resource template.`, 'Dismiss', { duration: 3500 });
        this.router.navigate(['/resources']);
        return;
      });
    } else {
      if (this.resourceConfig?.service) {
        const serviceInstance = this.injector.get(this.resourceConfig.service);
        if (serviceInstance) {

          const result = await serviceInstance.create(finalData, this.resourceConfig);

          this.logger.i("modified create method result:: ", result);
          this.snackBar.open(`Request to create ${this.resourceConfig?.name} received. You can now proceed to provision the resource template.`, 'Dismiss', { duration: 3500 });

          if (action == "Forward") {
            // alert("Forward ko call krna hai.");
            this.openForwardPopup(result);
            return;
          }

        }
      }
    }

    if (this.resourceConfig?.name != "Virtual Machine" && this.resourceConfig?.name != "User Account") {

      this.router.navigate([this.resourceConfig?.afterCreateRoute ?? '/resource-creator'],
        { state: { refresh: true } } // Pass refresh flag
      );
    }
  }



  selectedRequisitionId: string = "";
  showForwardPopup = false;
  selectedRequisition: any;
  redirectNavigationUrl: any;
  openForwardPopup(requisition: any) {

    this.selectedRequisitionId = requisition.id;
    this.selectedRequisition = requisition;
    this.redirectNavigationUrl = this.resourceConfig?.afterCreateRoute;
    this.showForwardPopup = true;
  }

  onForwarded(result: any) {
    this.logger.i("Forwarded requisition:", result);
    // this.loadRequisitions();
  }
}
